import { useQuery } from '@tanstack/react-query';

import { agentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

interface RetailerBucketApi {
  bucket?: string | undefined;
  versions?: { name?: string | undefined }[] | undefined;
}

interface RetailerPromptConfigApi {
  retailer?: string | undefined;
  buckets?: RetailerBucketApi[] | undefined;
}

export interface RetailerConfig {
  /** The slug sent as `retailer` on the product body. */
  retailer: string;
  /** Only the verticals this retailer ships a prompt for; every other vertical renders the shared one. */
  buckets: string[];
}

/**
 * Retailers that ship their own google-agent prompt, and the verticals each one covers.
 *
 * The prompt markdown in the response is dropped here — the picker needs slugs and buckets
 * only, and the rendered prompt is what the reader actually inspects. Baked into the deployed
 * image, so it cannot change within a session. `enabled` off for users who cannot pick one.
 */
export function useRetailerConfigs(enabled = true) {
  return useQuery<RetailerConfig[]>({
    queryKey: ['configuration', 'retailer-configs'],
    staleTime: Infinity,
    enabled,
    queryFn: async () => {
      const rows = await agentsClient
        .get(endpoints.agents.googleRetailerConfigs())
        .json<RetailerPromptConfigApi[]>();
      return rows
        .filter((r): r is RetailerPromptConfigApi & { retailer: string } => Boolean(r.retailer))
        .map((r) => ({
          retailer: r.retailer,
          buckets: (r.buckets ?? [])
            .map((b) => b.bucket ?? '')
            .filter(Boolean)
            .sort(),
        }));
    },
  });
}
